import { Dialog, Divider, makeStyles } from '@material-ui/core';
import React from 'react';
import { Download, X } from 'react-feather';
import { usePrefixTranslation } from '../../../i18n';
import { actions, useTypedDispatch, useTypedSelector } from '../../../redux';
import { theme } from '../../../theme/theme';
import { downloadFileDialog, getContentUrl } from '../../../util/file';
import { Button, IconButton, Property, StatusCard } from '../../global';

const useUnderReviewClasses = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        '& > hr': {
            background: theme.colors.neutral[3],
            margin: '10px 0 20px 0',
        },
    },
    content: {
        display: 'flex',
        flexDirection: 'column',
        flex: '1 1 auto',
        height: 0,
        overflowY: 'auto',
        '& > div': {
            margin: '10px 0',
        },
    },
    document: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '12px 20px',
        '& > div': {
            display: 'flex',
            flexDirection: 'column',
        },
        '& > div > span:first-child': {
            color: theme.colors.neutral[1],
            ...theme.typography.headings.h4,
            marginBottom: 0,
        },
        '& > div > span:last-child': {
            color: theme.colors.neutral[1],
            ...theme.typography.content.caption.paragraph,
        },
        '& > button': {
            color: theme.colors.neutral[3],
        },
    },
    property: {
        '& > span:last-child': {
            ...theme.typography.content.text,
        },
    },
    buttons: {
        display: 'flex',
        justifyContent: 'flex-end',
        margin: '0 -10px',
        '& > button': {
            margin: '0 10px',
        },
    },
    dialog: {
        '& .MuiDialog-paper': {
            display: 'flex',
            flexDirection: 'column',
            width: 520,
            height: 400,
            borderRadius: 6,
        },
    },
    dialogHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: 20,
        borderBottom: `1px solid ${theme.colors.neutral[9]}`,
        '& > span': {
            color: theme.colors.darkBlue[5],
            ...theme.typography.headings.h2,
            marginBottom: 0,
            textTransform: 'capitalize',
        },
        '& > button': {
            margin: -10,
            color: theme.colors.darkBlue[5],
        },
    },
    dialogProperty: {
        margin: 20,
    },
    dialogButton: {
        margin: 20,
        alignSelf: 'center',
    },
});

/**
 * Editor for projects in the under review mode.
 */
export const UnderReview = () => {
    const classes = useUnderReviewClasses();
    const { t } = usePrefixTranslation('project', { project: 'editor.UnderReview' });
    const dispatch = useTypedDispatch();
    const { project, users } = useTypedSelector(state => ({ project: state.project.changed, users: state.user.users }));
    const [dialogOpen, setDialogOpen] = React.useState(false);

    React.useEffect(() => void dispatch(actions.user.get()), []);

    if (!project) return <span>{'#project not in store'}</span>;

    return (
        <div className={classes.root}>
            <div className={classes.content}>
                <StatusCard className={classes.document} type='light' large>
                    <div>
                        <span>{project.report}</span>
                        <span>
                            {t('report-update', {
                                context: users.find(u => u.id === project.user) ? 'user' : 'no-user',
                                name: users.find(u => u.id === project.user)?.full_name ?? 'missing user name',
                                date: project.under_review_date,
                            })}
                        </span>
                    </div>
                    <IconButton
                        size='small'
                        title='download'
                        onClick={async () => {
                            if (project.report === '') return;
                            const url = await getContentUrl(project.report);
                            downloadFileDialog(url, 'report');
                        }}
                    >
                        <Download />
                    </IconButton>
                </StatusCard>
                <Property className={classes.property} name={t('input-comment')} value={project.findings} />
            </div>
            <Divider />
            <div className={classes.buttons}>
                <Button variant='outlined' onClick={() => setDialogOpen(true)}>
                    {t('button-request-update')}
                </Button>
                <Button
                    variant='contained'
                    disabled={project.report === ''}
                    onClick={() => {
                        dispatch(actions.project.edit({ status: 'completed' }));
                        dispatch(actions.project.commit());
                        dispatch(actions.project.update(true)).catch(error => {
                            dispatch(actions.project.edit({ status: 'under review' }));
                            dispatch(actions.project.commit());
                        });
                    }}
                >
                    {t('button-approve')}
                </Button>
            </div>
            <Dialog className={classes.dialog} open={dialogOpen} onClose={() => setDialogOpen(false)}>
                <div className={classes.dialogHeader}>
                    <span>{t('request-update')}</span>
                    <IconButton onClick={() => setDialogOpen(false)}>
                        <X />
                    </IconButton>
                </div>
                <Property
                    className={classes.dialogProperty}
                    name={t('input-feedback')}
                    value={project.comment}
                    multiline
                    rows={9}
                    editable
                    onChange={(name, value) => dispatch(actions.project.edit({ comment: value ?? '' }))}
                />
                <div style={{ flex: 1 }} />
                <Button
                    className={classes.dialogButton}
                    variant='contained'
                    onClick={async () => {
                        setDialogOpen(false);
                        const awaiting_report_date = project.awaiting_report_date;
                        dispatch(actions.project.edit({ status: 'awaiting report', awaiting_report_date: new Date() }));
                        dispatch(actions.project.commit());
                        try {
                            await dispatch(actions.project.update(true));
                        } catch (error) {
                            dispatch(actions.project.edit({ status: 'under review', awaiting_report_date }));
                            dispatch(actions.project.commit());
                        }
                    }}
                >
                    {t('button-send')}
                </Button>
            </Dialog>
        </div>
    );
};
